import React from "react";
import { View, StyleSheet, Platform } from "react-native";
import CustomText from "../../../components/common/general/CustomText/CustomText";
import { useEffect, useState } from "react";
import { MagnifyingGlass } from "../../../components/svgs/common";
import CoursesTable from "./CoursesTable";
import { PlusCircle } from "../../../components/svgIcons";
import CustomTextInput from "../../../components/common/general/CustomTextInput/CustomTextInput";
import {
  useSchoolsQuery,
  useGetCoursesBySchoolByQueryQuery,
  useAssignCourseToFacultyMutation,
  useCreateCoursePlaceholderMutation,
  useRemoveCourseAssignmentFromFacultyMutation,
  useDeleteCourseCascadeMutation,
  GetCoursesBySchoolByQueryDocument,
  CreateCoursePlaceholderMutation,
  useUploadCourseThumbnailMutation,
  GetEnrolledCoursesBySchoolDocument,
  GetNotEnrolledCoursesBySchoolDocument,
} from "../../../graphql/generated/graphql";
import Pagination from "../../../components/common/Pagination/Pagination";
import NewCustomAxioActionModal from "../UsersTabContainer/NewCustomAxioActionModal";
import AssignFacultyToCourseListContainer from "./AssignFacultyToCourseListContainer";
import RemoveFacultyFromCourseListContainer from "./RemoveFacultyFromCourseListContainer";
import AddCourseModal from "./AddCourseModalContainer";
import { NewCustomActionButton } from "../../../components/common/general/CustomButton/NewCustomActionButton";
import CourseRosterTabContainer from "../CourseRosterTabContainer.tsx/CourseRosterTabContainer";
import ImageUpload from "../../../components/common/general/ImageUpload/ImageUpload";
import CustomModal from "../../../components/common/general/CustomModal/CustomModal";
import CustomButton from "../../../components/common/general/CustomButton/CustomButton";
import CourseDegreesTabContainer from "../CourseDegreesTabContainer/CourseDegreesTabContainer";

const modals = {
  removeTeacher: {
    title: "Remove Faculty from Course?",
    ariaLabel: "Remove Faculty From Course Modal",
    warningText:
      "Following faculty will no longer have access to the course. This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Remove from Course",
  },
  deleteCourse: {
    title: "Delete Course?",
    ariaLabel: "Delete Course Modal",
    warningText:
      "Are you sure you want to permanently delete this course? This action cannot be undone, and all associated data will be deleted.",
    actionButtonText: "Delete Course",
  },
  deleteConfirmation: {
    title: "Course Deleted",
    ariaLabel: "Course Deleted Modal",
    warningText: "The course has been successfully deleted.",
    actionButtonText: "OK",
  },
  imageUpdateConfirmation: {
    title: "Success!",
    ariaLabel: "Course Image Updated Successfully Modal",
    warningText: "The course image has been successfully updated.",
    actionButtonText: "OK",
  },
  imageUpdateError: {
    title: "Error!",
    ariaLabel: "Course Image Update Error Modal",
    warningText: "An error occurred while updating the course image.",
    actionButtonText: "CLOSE",
  },
};

export enum CoursesTabModalState {
  CLOSED = "CLOSED",
  DELETE_COURSE = "DELETE_COURSE",
  DELETE_CONFIRMATION = "DELETE_CONFIRMATION",
  ASSIGN_FACULTY = "ASSIGN_FACULTY",
  REMOVE_FACULTY = "REMOVE_FACULTY",
  ADD_COURSE = "ADD_COURSE",
  COURSE_ROSTER = "COURSE_ROSTER",
  COURSE_DEGREES = "COURSE_DEGREES",
  EDIT_IMAGE = "EDIT_IMAGE",
  IMAGE_UPDATE_CONFIRMATION = "IMAGE_UPDATE_CONFIRMATION",
  IMAGE_UPDATE_ERROR = "IMAGE_UPDATE_ERROR",
  GENERATE_COURSE = "GENERATE_COURSE",
}

const CoursesTabContainer = ({ customColor }) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);
  const [modalState, setModalState] = useState({
    state: CoursesTabModalState.CLOSED,
    courseId: "",
    courseName: "",
  });

  const { data: schoolsData } = useSchoolsQuery();

  const { data: coursesData, refetch: coursesRefetch } = useGetCoursesBySchoolByQueryQuery({
    variables: {
      schoolId: schoolsData?.getSchools[0].id,
      query: searchQuery,
      page: page,
      pageSize: pageSize,
    },
  });

  const refetchQueries = [
    {
      query: GetCoursesBySchoolByQueryDocument,
      variables: {
        schoolId: schoolsData?.getSchools[0].id,
        query: searchQuery,
        page: page,
        pageSize: pageSize,
      },
    },
    {
      query: GetEnrolledCoursesBySchoolDocument,
      variables: {
        schoolId: schoolsData?.getSchools[0].id,
      },
    },
    {
      query: GetNotEnrolledCoursesBySchoolDocument,
      variables: {
        schoolId: schoolsData?.getSchools[0].id,
      },
    },
  ];

  const [assignCourseToFaculty] = useAssignCourseToFacultyMutation();
  const [removeCourseFromFaculty] = useRemoveCourseAssignmentFromFacultyMutation();
  const [createCoursePlaceholder] = useCreateCoursePlaceholderMutation();
  const [deleteCourseCascade] = useDeleteCourseCascadeMutation();

  const addCourse = async ({
    courseName,
    courseType,
    degrees,
    description,
    objectives,
    targetAudience,
    cost,
    startDate,
  }: {
    courseName?: string;
    courseType?: "Core" | "General" | "Elective";
    degrees?: string[];
    description?: string;
    objectives?: string;
    targetAudience?: string;
    cost?: number;
    startDate?: Date;
  }) => {
    try {
      const result = await createCoursePlaceholder({
        variables: {
          name: courseName,
          type: courseType,
          schoolId: schoolsData?.getSchools[0].id,
          description,
          objectives,
          targetAudience,
          cost,
          startDate: startDate.toString(),
          degreeIds: degrees,
        },
        refetchQueries: refetchQueries,
      });

      return result.data;
    } catch (error) {
      return {
        createCoursePlaceholder: {
          data: null,
          success: false,
        },
      } as unknown as CreateCoursePlaceholderMutation;
    }
  };

  const assignFaculty = async (facultyId: string) => {
    await assignCourseToFaculty({
      variables: {
        courseId: modalState.courseId,
        userId: facultyId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: CoursesTabModalState.CLOSED, courseId: "", courseName: "" });
  };

  const removeFaculty = async (facultyId: string) => {
    await removeCourseFromFaculty({
      variables: {
        courseId: modalState.courseId,
        userId: facultyId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: CoursesTabModalState.CLOSED, courseId: "", courseName: "" });
  };

  const handleDeleteCourse = async () => {
    await deleteCourseCascade({
      variables: {
        id: modalState.courseId,
      },
      refetchQueries: refetchQueries,
    });
    setModalState({ state: CoursesTabModalState.DELETE_CONFIRMATION, courseId: "", courseName: "" });
    setSearchQuery("");
  };

  const handleActionModalOpen = async (courseId: string, courseName: string, modalType: CoursesTabModalState) => {
    setModalState({ state: modalType, courseId: courseId, courseName: courseName });
  };

  const handleModalClose = () => {
    setModalState({ state: CoursesTabModalState.CLOSED, courseId: "", courseName: "" });
  };

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= coursesData?.getCoursesBySchoolByQuery?.data.totalCount) {
      setPage(newPage);
    }
  };

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    coursesRefetch();
  }, [searchQuery, page, pageSize]);

  const [uploadThumbnail] = useUploadCourseThumbnailMutation();

  const handleImageUpload = async (base64Image: string) => {
    setIsSubmitting(true);
    try {
      const base64Data = base64Image.split(",")[1];
      const contentType = base64Image.split(";")[0].split(":")[1];
      const size = Math.ceil((base64Data.length * 3) / 4);

      const response = await uploadThumbnail({
        variables: {
          input: {
            image: base64Data,
            contentType,
            size,
            courseId: modalState.courseId,
          },
        },
        refetchQueries: refetchQueries,
      });

      if (response.data?.uploadCourseThumbnail?.success) {
        setModalState((prev) => ({
          ...prev,
          state: CoursesTabModalState.IMAGE_UPDATE_CONFIRMATION,
        }));
      } else {
        setModalState((prev) => ({
          ...prev,
          state: CoursesTabModalState.IMAGE_UPDATE_ERROR,
        }));
      }
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error uploading image:", error);
      setModalState((prev) => ({
        ...prev,
        state: CoursesTabModalState.IMAGE_UPDATE_ERROR,
      }));
      setIsSubmitting(false);
    }
  };

  if (modalState.state === CoursesTabModalState.COURSE_ROSTER) {
    return (
      <CourseRosterTabContainer
        customColor={customColor}
        course={coursesData?.getCoursesBySchoolByQuery?.data.courses.find(
          (course) => course.id === modalState.courseId
        )}
        onBack={() => setModalState((prev) => ({ ...prev, state: CoursesTabModalState.CLOSED }))}
      />
    );
  }

  if (modalState.state === CoursesTabModalState.COURSE_DEGREES) {
    return (
      <CourseDegreesTabContainer
        course={coursesData?.getCoursesBySchoolByQuery?.data.courses.find(
          (course) => course.id === modalState.courseId
        )}
        onBack={() => setModalState((prev) => ({ ...prev, state: CoursesTabModalState.CLOSED }))}
      />
    );
  }

  return (
    <>
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={styles.headerRow}>
            <CustomText text="Manage Courses" size="l" weight="bold" style={styles.headerText} />
            <View style={styles.buttonGroup}>
              <NewCustomActionButton
                text="Add a Course"
                onPress={() => setModalState({ state: CoursesTabModalState.ADD_COURSE, courseId: "", courseName: "" })}
                leftIcon={<PlusCircle color={customColor} />}
              />
            </View>
          </View>
          <View style={styles.headerRow}>
            <View style={styles.subHeaderContainer}>
              <CustomText text="Total Courses" size="m" weight="semiBold" style={styles.subHeaderText} />
              <CustomText
                text={`(${coursesData?.getCoursesBySchoolByQuery.data.totalCount || 0})`}
                size="m"
                weight="semiBold"
                style={styles.subHeaderText}
                useTranslationText={false}
              />
            </View>
            <View style={styles.searchContainer}>
              <MagnifyingGlass style={styles.searchIcon} />
              <CustomTextInput
                placeholder="Search by any value"
                value={searchQuery}
                onChangeText={setSearchQuery}
                style={[
                  styles.searchInput,
                  Platform.select({
                    web: {
                      outlineStyle: "none",
                      outlineWidth: 0,
                      WebkitAppearance: "none",
                    } as unknown,
                  }),
                ]}
                placeholderTextColor="#9CA3AF"
                selectionColor="#6B7280"
                cursorColor="#6B7280"
              />
            </View>
          </View>
        </View>
        <CoursesTable
          courses={coursesData?.getCoursesBySchoolByQuery?.data.courses || []}
          onActionModalOpen={handleActionModalOpen}
        />
        <Pagination
          page={page}
          handlePageChange={handlePageChange}
          totalPageCount={Math.ceil(coursesData?.getCoursesBySchoolByQuery?.data.totalCount / pageSize)}
        />
      </View>
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.DELETE_COURSE}
        onClose={handleModalClose}
        onAction={handleDeleteCourse}
        modalTexts={modals.deleteCourse}
        contextualText={modalState.courseName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.DELETE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.deleteConfirmation}
        contextualText={modalState.courseName}
      />
      <AssignFacultyToCourseListContainer
        visible={modalState.state === CoursesTabModalState.ASSIGN_FACULTY}
        onClose={handleModalClose}
        onAction={assignFaculty}
      />
      <RemoveFacultyFromCourseListContainer
        visible={modalState.state === CoursesTabModalState.REMOVE_FACULTY}
        onClose={handleModalClose}
        onAction={removeFaculty}
        courseId={modalState.courseId}
      />
      <AddCourseModal
        visible={modalState.state === CoursesTabModalState.ADD_COURSE}
        onClose={handleModalClose}
        onSubmit={addCourse}
      />
      {/* Add this new modal condition */}
      {modalState.state === CoursesTabModalState.EDIT_IMAGE && (
        <CustomModal
          visible={true}
          onRequestClose={handleModalClose}
          title="Edit Course Image"
          ariaLabel="Edit Course Image Modal"
          showCloseButton={true}
          style={{ width: "90%" }}
          exitFill={undefined}
          onShow={() => {}}>
          <View style={styles.modalContent}>
            <ImageUpload
              onChange={handleImageUpload}
              aspectRatio={[16, 9]}
              buttonText="Upload Course Image"
              changeButtonText="Change Course Image"
              dropzoneText="Drag & drop course image here"
              dropzoneSubtext="Recommended: 1792x1024px. JPG or PNG format"
            />
            <View style={styles.buttonContainer}>
              <CustomButton
                text="Cancel"
                onPress={handleModalClose}
                styleType="secondary"
                size="m"
                style={isSubmitting ? { backgroundColor: "#ccc" } : styles.cancelButton}
                textStyle={styles.cancelButtonText}
                disabled={isSubmitting}
              />
            </View>
          </View>
        </CustomModal>
      )}
      {/* Add these new modals */}
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.IMAGE_UPDATE_CONFIRMATION}
        onClose={handleModalClose}
        modalTexts={modals.imageUpdateConfirmation}
        contextualText={modalState.courseName}
      />
      <NewCustomAxioActionModal
        visible={modalState.state === CoursesTabModalState.IMAGE_UPDATE_ERROR}
        onClose={handleModalClose}
        modalTexts={modals.imageUpdateError}
        contextualText={modalState.courseName}
      />
    </>
  );
};

const styles = StyleSheet.create({
  container: {
    padding: 24,
    backgroundColor: "#fff",
    borderRadius: 8,
    flex: 1,
  },
  header: {
    flexDirection: "column",
    width: "100%",
    marginBottom: 24,
  },
  headerRow: {
    flexDirection: "row",
    width: "100%",
    alignItems: "center",
    justifyContent: "space-between",
    paddingVertical: 16,
  },
  buttonGroup: {
    flexDirection: "row",
    gap: 12,
  },
  headerText: {
    color: "#000",
  },
  subHeaderContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  subHeaderText: {
    color: "#000",
  },
  searchContainer: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E5E7EB",
    borderRadius: 6,
    padding: 4,
  },
  buttonContainer: {
    flexDirection: "row",
    justifyContent: "flex-start",
    gap: 12,
    marginTop: 8,
  },
  searchIcon: {
    marginRight: 8,
    color: "#3e68fe",
  },
  searchInput: {
    borderWidth: 0,
    backgroundColor: "transparent",
    width: 200,
    padding: 8,
    fontSize: 14,
    color: "#374151",
  },
  modalContent: {
    padding: 24,
    width: "100%",
  },
  cancelButton: {
    backgroundColor: "transparent",
    borderWidth: 1,
    borderColor: "#D1D5DB",
    borderRadius: 6,
    paddingHorizontal: 16,
  },

  cancelButtonText: {
    color: "#4B5563",
  },
});

export default CoursesTabContainer;
